import { useState } from 'react';
import { Outlet, Link as RouterLink, useNavigate } from 'react-router';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../hooks/useAuth';
import { usePageView } from '../hooks/usePageView';
import { useHashScroll } from '../hooks/useHashScroll';
import { useCookieConsent } from '../hooks/useCookieConsent';
import CookieConsentBanner from '../components/CookieConsentBanner';
import bindlesLogo from '../assets/bindles-primary.svg';
import { useProducts } from '../hooks/useProducts';
import { useActivePromotion } from '../hooks/usePromotions';
import { useDismissedPromotion } from '../hooks/useDismissedPromotion';
import PromotionBanner from '../components/PromotionBanner';
import trackEvent, { AnalyticsEvent } from '../utils/trackEvent';

export default function PublicLayout() {
  const { user, logout } = useAuth();
  const { resetConsent } = useCookieConsent();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: products } = useProducts();
  const { data: activePromotion } = useActivePromotion();
  const { dismissedId, clearDismiss } = useDismissedPromotion();
  usePageView();
  useHashScroll();

  const handleLogout = () => {
    logout();
    void navigate('/');
  };

  const toggleDrawer = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PromotionBanner />
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{ bgcolor: 'background.default' }}
      >
        <Toolbar>
          <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <Box component="img" src={bindlesLogo} alt="bindles" sx={{ height: 40 }} />
          </Box>

          {/* Desktop nav */}
          <Box component="nav" sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }}>
            <Stack direction="row" spacing={2}>
              <Button component={RouterLink} to="/#products" color="inherit">
                Products
              </Button>
              <Button component={RouterLink} to="/about" color="inherit">
                About
              </Button>
              <Button component={RouterLink} to="/blog" color="inherit">
                Blog
              </Button>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {user ? (
              <>
                <Button component={RouterLink} to="/app/dashboard" variant="contained">
                  Dashboard
                </Button>
                <Button onClick={handleLogout} color="inherit">
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button component={RouterLink} to="/login" color="inherit">
                  Sign In
                </Button>
                <Button component={RouterLink} to="/register" variant="contained">
                  Get Started
                </Button>
              </>
            )}
          </Stack>

          {/* Mobile hamburger */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            edge="end"
            onClick={toggleDrawer}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer}>
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer}>
          <List>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/#products">
                <ListItemText primary="Products" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/about">
                <ListItemText primary="About" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/contact">
                <ListItemText primary="Contact" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/blog">
                <ListItemText primary="Blog" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List>
            {user ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/app/dashboard">
                    <ListItemText primary="Dashboard" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemText primary="Sign Out" />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/login">
                    <ListItemText primary="Sign In" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/register">
                    <ListItemText primary="Get Started" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      <Box component="footer" sx={{ py: 4, borderTop: 1, borderColor: 'divider' }}>
        <Container>
          {products && (
            <Stack
              direction="row"
              spacing={3}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mb: 2 }}
            >
              {products.map((product) => (
                <Link
                  component={RouterLink}
                  to={`/products/${product.slug}`}
                  variant="body2"
                  color="text.secondary"
                >
                  <Badge color="warning" variant="dot" invisible={product.is_published}>
                    {product.name}
                  </Badge>
                </Link>
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/about" variant="body2" color="text.secondary">
              About
            </Link>
            <Link component={RouterLink} to="/pricing" variant="body2" color="text.secondary">
              Pricing
            </Link>
            <Link component={RouterLink} to="/blog" variant="body2" color="text.secondary">
              Blog
            </Link>
            <Link component={RouterLink} to="/faq" variant="body2" color="text.secondary">
              FAQ
            </Link>
            <Link component={RouterLink} to="/contact" variant="body2" color="text.secondary">
              Contact
            </Link>
            <Link
              component="button"
              variant="body2"
              color="text.secondary"
              onClick={resetConsent}
              sx={{ cursor: 'pointer' }}
            >
              Cookie Settings
            </Link>
            {activePromotion && dismissedId === activePromotion.id && (
              <Link
                component="button"
                variant="body2"
                color="text.secondary"
                onClick={() => {
                  clearDismiss();
                  trackEvent(AnalyticsEvent.PROMOTION_FOOTER_REOPEN, {
                    promotion_id: activePromotion.id,
                    code: activePromotion.code,
                  });
                }}
                sx={{ cursor: 'pointer' }}
              >
                View Current Promotions
              </Link>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            &copy; {new Date().getFullYear()} bindles.io. All rights reserved.
          </Typography>
        </Container>
      </Box>

      <CookieConsentBanner />
    </Box>
  );
}
